import { useReactiveVar } from '@apollo/client';
import _ from 'lodash';
import React, { useState } from 'react';
import { userPrivileges, userTier } from '../../../../apollo/cache';
import PrivilegeNames from '../../../../constants/privilegeNames';
import ApolloError from '../../../shared/components/ApolloError';
import LinkButton from '../../../shared/components/LinkButton';
import PageInstructions from '../../../shared/components/PageInstructions';
import SuccessMessage from '../../../shared/components/SuccessMessage';
import * as LicenseTypes from '../../constants/LicenseTypes';
import NumberOfLicenses from '../shared/NumberOfLicenses';
import { useUserPaidDataQuery } from '../shared/hooks/useUserPaidDataQuery';
import {
  getLicenseCount,
  getUserLicenseInformation
} from '../shared/services/userLicenseInformationService';
import LicensesTable from '../shared/components/LicensesTable';
import { PAGE_MODE } from '../../constants/pageModeTypes';

/**
 * @typedef {object} UserPaidLicensesTableProps
 * @property {string} [licensesModifiedSuccessfullyMessage]
 * @property {number} [orgId]
 * @property {string} [orgName]
 * @property {number} [orgUnitId]
 * @property {number} pageMode
 */

/**
 * @typedef {object} UserLicenseInformation
 * @property {number} [licenseCount]
 * @property {number} [assignedLicenseCount]
 * @property {Array<*>} [users]
 * @property {number} [orgUnitLicenseCount]
 * @property {number} [assignedOrganizationOrgUnitLicenseCount]
 * @property {Array<*>} [organizationOrgUnitUsers]
 */

/**
 * @param {UserPaidLicensesTableProps} props
 */
const UserPaidLicensesTable = props => {
  const {
    licensesModifiedSuccessfullyMessage,
    pageMode,
    orgId = null,
    orgName = '',
    orgUnitId = null
  } = props;

  const currentUserTier = useReactiveVar(userTier);
  const privileges = useReactiveVar(userPrivileges);

  const [userLicenseInformation, setUserLicenseInformation] = useState(
    /** @type {UserLicenseInformation} */ ({})
  );

  const isLoading = _.isEmpty(userLicenseInformation);
  const displayPaidTwoTables =
    pageMode === PAGE_MODE.MddapOrg || pageMode === PAGE_MODE.ISACAVipOrg;

  const getInstructions = () => {
    if (pageMode !== PAGE_MODE.MddapOrg) {
      return (
        'The table below displays all paid licenses for your organization. ' +
        'The table can be sorted or filtered depending on the column. ' +
        'Licenses can be assigned or edited after assignment.'
      );
    }

    return (
      'The tables below display all paid licenses. ' +
      'The first table lists all organization licenses and the second table ' +
      'lists all organizational unit licenses. ' +
      'Both tables can be sorted or filtered depending on the column. ' +
      'Licenses can be assigned or edited after assignment.'
    );
  };

  const getUsersCallback = ({
    orgUsers,
    orgUnitUsers,
    orgLicenseCount,
    orgLicenseAssignedCount,
    orgUnits,
    orgUnitLicenseCount,
    orgUnitLicenseAssignedCount
  }) => {
    const userLicenseInformation = getUserLicenseInformation(
      orgUsers || orgUnitUsers,
      displayPaidTwoTables && orgUnitUsers,
      getLicenseCount(orgLicenseCount, orgUnitLicenseCount),
      getLicenseCount(orgLicenseAssignedCount, orgUnitLicenseAssignedCount),
      orgUnits,
      pageMode,
      orgUnitId,
      currentUserTier,
      false
    );
    setUserLicenseInformation(userLicenseInformation);
  };

  const { error } = useUserPaidDataQuery(orgId, orgUnitId, getUsersCallback);

  if (error) {
    return <ApolloError />;
  }

  return (
    <div className='user-table-of-licenses'>
      <div className='page-header'>
        <PageInstructions
          title='User Management - Paid Licenses Instructions'
          description={getInstructions()}
        />
        {privileges.includes(PrivilegeNames.LicenseManagement) && (
          <LinkButton
            data-testid='manage-button'
            className='primary-button sm'
            to='/user-management/license-management'
            state={{
              activeTab: LicenseTypes.PAID.value,
              orgId: orgId,
              orgName: orgName,
              pageMode: pageMode
            }}>
            Manage
          </LinkButton>
        )}
      </div>
      {licensesModifiedSuccessfullyMessage && (
        <SuccessMessage message={licensesModifiedSuccessfullyMessage} />
      )}
      <NumberOfLicenses
        numberOfTotalLicenses={userLicenseInformation.licenseCount}
        numberOfAssignedLicenses={userLicenseInformation.assignedLicenseCount}
        loading={isLoading}
      />
      <LicensesTable
        loading={isLoading}
        usersData={userLicenseInformation.users}
        orgId={orgId}
        orgName={orgName}
        pageMode={pageMode}
      />

      {displayPaidTwoTables && (
        <>
          <NumberOfLicenses
            numberOfTotalLicenses={userLicenseInformation.orgUnitLicenseCount}
            numberOfAssignedLicenses={
              userLicenseInformation.assignedOrganizationOrgUnitLicenseCount
            }
            loading={isLoading}
          />
          <LicensesTable
            loading={isLoading}
            usersData={userLicenseInformation.organizationOrgUnitUsers}
            orgId={orgId}
            orgName={orgName}
            pageMode={
              pageMode === PAGE_MODE.ISACAVipOrg ? PAGE_MODE.ISACAAppraiser : PAGE_MODE.OrgUnit
            }
            singleUnit={true}
          />
        </>
      )}
    </div>
  );
};

export default UserPaidLicensesTable;
